import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Stack, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { v4 as uuidv4 } from 'uuid';

import { PhoneBase, InputUI, ButtonUI, CheckboxUI, SwitchUI } from '../Interface';
import itemService from '../../service/item.service';
import { useLoader } from '../../provider/LoaderProvider';
import SearchDialog from './SearchDialog';
import { DocumentType } from '../../config/DocumentType';
import dayjs from 'dayjs';
import useConfig from '../../context/ConfigContext';
import useParam from '../../context/ParamContext';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import serviceCoreService from '../../service/serviceCore.service';
import userService from '../../service/user.service';
import { cleanValues } from '../../utils/helper';
import PinCodeLogInDialog from '../Interface/PinCodeLogInDialog';
import ItemsDialog from './ItemsDialog';
import WarningDialog from './WarningDialog';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';

const ServiceIssue = () => {
  const timeoutRef = useRef(null);
  const itemInputRef = useRef(null);
  const { config, getConfig } = useConfig();
  const { getParam, params } = useParam();
  const { showLoader, hideLoader } = useLoader();
  const [openSearch, setOpenSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  // add states for items and quantity handling
  const [itemFilter, setItemFilter] = useState('');
  const [docLines, setDocLines] = useState([]);
  const [fullSearch, setFullSearch] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [warningDialogData, setWarningDialogData] = useState({ open: false, data: {} });

  const defLogInData = {
    pinCode: '',
    userId: null,
  };
  const [logInData, setLogInData] = useState(defLogInData);
  const [showLogInDialog, setShowLogInDialog] = useState(true);

  useEffect(() => {
    Object.keys(config).length === 0 && getConfig();
    Object.keys(params).length === 0 && getParam();
  }, []);

  useEffect(() => {
    setDocLines([]);
  }, [selectedService]);

  useEffect(() => {
    if (logInData.pinCode) {
      getUserByPinCode();
    } else {
      setShowLogInDialog(true);
    }
  }, [logInData.pinCode]);

  useEffect(() => {
    if (!logInData.pinCode || !params.SERVICE_ACTIVITY) {
      return;
    }

    const handleActivity = () => resetTimer();

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('scroll', handleActivity);
    window.addEventListener('touchstart', handleActivity);

    resetTimer();

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      clearTimeout(timeoutRef.current);
    };
  }, [logInData.pinCode, params]);

  const defaultDocumentLine = {
    lineStatus: 'C',
    itemCode: null,
    itemName: null,
    itemCodeInBp: null,
    qty: null,
    cplQty: null,
    opnQty: null,
    quantityUnitId: null,
    quantityUnitCode: null,
    quantityUnitName: null,
    whsCode: null,
    toWhsCode: null,
    unitPrice: null,
    currencyCode: null,
    vatCode: null,
    vatPercent: null,
    vatValue: null,
    netPrice: null,
    grossPrice: null,
    dueDate: dayjs(),
    vatValueInOwnCur: null,
    netPriceInOwnCur: null,
    grossPriceInOwnCur: null,
    weightChangeNumber: null,
    weight: null,
    projectId: null,
    costCenterCode: null,
    itemReference: null,
    internalNote: '',
    bpNote: '',
    isBillable: false,
    baseId: null,
    baseLine: null,
    baseDocnum: null,
    baseDocType: null,
    warranty: false,
    docLineBatchSn: [],
  };

  const defaultValues = {
    docTypeId: DocumentType.IssueForService,
    docStatus: 'C',
    stornoId: null,
    docDate: dayjs(),
    issueDate: dayjs(),
    dueDate: dayjs(),
    companyId: null,
    companyCode: '',
    companyName: '',
    companyContactId: null,
    shortName: '',
    incotermCity: '',
    companyWarning: '',
    currencyCode: null,
    exchangeRate: null,
    paymentMethodId: null,
    reference: '',
    projectId: null,
    costCenterCode: null,
    salesOrPurchasePersonId: null,
    billingAdressId: null,
    billingCountry: '',
    billingProvince: '',
    billingPostCode: '',
    billingCity: '',
    billingStreetAddress: '',
    shippingAdressId: null,
    shippingCountry: '',
    shippingProvince: '',
    shippingPostCode: '',
    shippingCity: '',
    shippingStreetAddress: '',
    deliveryMethodId: null,
    incotermCode: null,
    whsCode: config?.defaultWarehouse || null,
    toWhsCode: null,
    priceListId: null,
    langCode: null,
    hunTaxNumber: null,
    euTaxNumber: null,
    foreignTaxNumber: null,
    sumInDocCur: null,
    netInDocCur: null,
    VATInDocCur: null,
    roundingInDocCur: null,
    sumInOwnCur: null,
    netInOwnCur: null,
    VATInOwnCur: null,
    roundingInOwnCur: null,
    note: '',
    internalNote: '',
    docLine: [],
  };

  const handleQtyChange = (id) => (e) => {
    const val = Math.max(0, Number(e.target.value));
    setDocLines((prev) => prev.map((l) => (l.id === id ? { ...l, qty: val } : l)));
  };

  const handleBillableChange = (id) => (e) => {
    const checked = e.target.checked;
    setDocLines((prev) => prev.map((l) => (l.id === id ? { ...l, isBillable: checked } : l)));
  };

  const handleWarrantyChange = (id) => (e) => {
    const checked = e.target.checked;
    setDocLines((prev) => prev.map((l) => (l.id === id ? { ...l, warranty: checked } : l)));
  };

  const resetTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setLogInData(defLogInData);

      resetTimer();
    }, params.SERVICE_ACTIVITY);
  };

  const getUserByPinCode = () => {
    userService.getUserByPinCode(logInData.pinCode).then((data) => {
      if (Object.keys(data).length > 0) {
        setLogInData((prev) => ({
          ...prev,
          userId: data.userId,
        }));
        setShowLogInDialog(false);
        setOpenSearch(true);
      } else {
        addNotification({
          content: 'Nem található felhasználó ezzel a PIN kóddal!',
          type: NotificationType.ERROR,
        });
      }
    });
  };

  const handleOpenSearch = () => {
    setOpenSearch(true);
    setSearchQuery('');
  };

  const handleCloseSearch = () => {
    setOpenSearch(false);
    setTimeout(() => {
      if (itemInputRef.current) itemInputRef.current.focus();
    }, 0);
  };

  const addItem = (data) => {
    const newLine = {
      ...defaultDocumentLine,
      id: uuidv4(),
      itemCode: data.itemCode,
      itemName: data.itemName,
      qty: 1,
      whsCode: config.defaultWarehouse || null,
      quantityUnitId: data.quantityUnit?.id,
      quantityUnitCode: data.quantityUnit?.code,
      quantityUnitName: data.quantityUnit?.name,
      baseId: selectedService?.id,
      baseLine: null,
      baseDocnum: selectedService?.workingNumber,
      baseDocType: null,
      // isBillable already false by defaultDocumentLine; override here if needed later
    };
    setDocLines((prev) => [...prev, newLine]);
    setItemFilter('');
  };

  const checkAlreadyIssuedItem = (val) => {
    showLoader();
    serviceCoreService
      .checkAlreadyIssuedItem({ itemCode: val, projectId: selectedService?.projectId })
      .then((data) => {
        if (data.warning) {
          setWarningDialogData({
            open: true,
            data: {
              message: data.message,
            },
          });
        }
        addItem(data);
      })
      .finally(hideLoader);
  };

  useEffect(() => {
    if (selectedItem) {
      checkAlreadyIssuedItem(selectedItem.itemCode);
      setSelectedItem(null);
      setItemFilter('');
    }
  }, [selectedItem]);

  const searchItem = () => {
    const val = itemFilter.trim();
    if (!val) return;
    showLoader();
    itemService
      .findItem({ query: val, fullSearch })
      .then((data) => {
        if (data.length > 1) {
          setItems(data);
          return;
        }
        if (!data || !data?.[0]?.itemCode) {
          addNotification({
            content: `Nincs találat a megadott cikkre. (${val})`,
            type: NotificationType.WARNING,
          });

          setTimeout(() => {
            setItemFilter('');
            if (itemInputRef.current) itemInputRef.current.focus();
          }, 500);

          return;
        }

        if (data?.length === 1 && data?.[0]?.itemCode) {
          setSelectedItem(data[0]);
        }
      })
      .finally(hideLoader);
  };

  const handleRemoveLine = (id) => {
    setDocLines((prev) => prev.filter((l) => l.id !== id));
  };

  // validate and submit
  const handleRegister = () => {
    const errors = [];
    if (!selectedService) errors.push('Nincs kiválasztott szervíz.');
    if (docLines.length === 0) errors.push('Nincs hozzáadott cikk.');
    if (docLines.some((l) => Number(l.qty) <= 0)) errors.push('Minden mennyiségnek 0-nál nagyobbnak kell lennie.');

    if (errors.length) {
      addNotification({
        content: errors.join(' /n'),
        type: NotificationType.ERROR,
      });
      return;
    }

    const doc = {
      ...cleanValues(defaultValues),
      docLine: docLines.map((l, index) => cleanValues({ ...l, visOrder: index + 1 })),
      createdBy: logInData.userId,
    };

    serviceCoreService.createDocument(doc).then(() => {
      addNotification({
        content: 'A szerviz kiadás sikeresen rögzítve lett.',
        type: NotificationType.SUCCESS,
      });
      setSelectedService(null);
      setDocLines([]);
      setSearchQuery('');
      setOpenSearch(true);
    });
  };

  const isValid = !!selectedService && docLines.length > 0 && docLines.every((l) => Number(l.qty) > 0);

  return (
    <PhoneBase name="ServiceIssue" sx={{ p: 2 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Kiadás szervizre
      </Typography>
      <Box>
        <Box>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              minHeight: 80,
              mt: 1.5,
              p: 1.5,
              bgcolor: 'grey.100',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h7" component="h4">
                Szervíz munka
              </Typography>
              <IconButton size="small" onClick={handleOpenSearch} aria-label="Keresés">
                <SearchIcon fontSize="small" />
              </IconButton>
            </Box>

            {selectedService && (
              <Box
                key={selectedService.id}
                sx={{
                  px: 1.5,
                  py: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: 'white',
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                  {selectedService.workingNumber} #{selectedService.id}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Projekt:
                  </Box>{' '}
                  {selectedService.project?.projectName}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {getProjectOrderNumberName(params)}:
                  </Box>{' '}
                  {selectedService.project?.orderNumber}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Szerviz típus:
                  </Box>{' '}
                  {selectedService.serviceTaskType?.name}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            minHeight: 80,
            mt: 1.5,
            p: 1.5,
            bgcolor: 'grey.100',
          }}
        >
          <Typography variant="h7" component="h4">
            Cikkek
          </Typography>

          {/* item filter input */}
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, bgcolor: 'white' }}>
              <InputUI
                autoFocus
                fullWidth
                size="small"
                placeholder={fullSearch ? 'Cikknév/cikkszám' : 'Cikkszám/vonalkód'}
                value={itemFilter}
                onChange={(e) => setItemFilter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    searchItem();
                  }
                }}
                inputRef={itemInputRef}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" edge="end" aria-label="Keresés" onClick={searchItem}>
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <SwitchUI checked={fullSearch} onChange={(e) => setFullSearch(e.target.checked)} />
          </Box>

          {/* rögzített cikkek */}
          <Stack spacing={0.25} sx={{ mt: 1.5 }}>
            {docLines.map((item) => {
              const key = item.id;
              return (
                <Box
                  key={key}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'white',
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 0.5,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                        {item.itemCode} - {item.itemName}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InputUI
                        name="qty"
                        type="number"
                        size="small"
                        sx={{ width: 110 }}
                        inputProps={{ min: 0, step: 1 }}
                        value={item.qty ?? 0}
                        onChange={handleQtyChange(key)}
                      />
                      <IconButton color="error" size="small" aria-label="Törlés" onClick={() => handleRemoveLine(key)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    <Box>
                      <CheckboxUI label="Számlázandó" checked={item.isBillable} onChange={handleBillableChange(key)} />
                      <CheckboxUI label="Garanciális" checked={item.warranty} onChange={handleWarrantyChange(key)} />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
        <Box textAlign="center" mt="2rem">
          <ButtonUI text="Rögzítés" className="bg-success" onClick={handleRegister} disabled={!isValid} />
        </Box>
      </Box>

      <PinCodeLogInDialog
        open={showLogInDialog}
        handleClose={() => setShowLogInDialog(false)}
        logInData={logInData}
        setLogInData={setLogInData}
      />

      <SearchDialog
        open={openSearch}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        handleClose={handleCloseSearch}
        setSelectedService={setSelectedService}
      />

      <ItemsDialog
        open={items.length > 0}
        handleClose={() => {
          setItems([]);
          setItemFilter('');
          if (itemInputRef.current) itemInputRef.current.focus();
        }}
        items={items}
        addItem={(item) => {
          setSelectedItem(item);
        }}
      />

      <WarningDialog
        dialogData={warningDialogData}
        handleClose={() => setWarningDialogData({ open: false, data: {} })}
      />
    </PhoneBase>
  );
};

export default ServiceIssue;
